/**
 * Utility functions for detecting and handling chunk loading errors
 *
 * Chunk loading errors occur when:
 * - Old chunk files are deleted during deployment
 * - User's browser tries to load chunks referenced in cached HTML
 * - Results in 404 errors that need to be handled gracefully
 */

/**
 * Detects if an error is a chunk loading failure
 *
 * Common patterns:
 * - "Failed to fetch dynamically imported module"
 * - "Loading chunk X failed"
 * - "Loading CSS chunk X failed"
 * - Network errors (404) when loading .js/.css files
 * - ChunkLoadError from webpack/vite
 *
 * @param error - The error to check
 * @returns true if the error is a chunk loading error
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? error.stack : '';
  const errorName = error instanceof Error ? error.name : '';

  const errorString =
    `${errorName} ${errorMessage} ${errorStack}`.toLowerCase();

  // Check for common chunk loading error patterns
  const chunkErrorPatterns = [
    'failed to fetch dynamically imported module',
    'loading chunk',
    'loading css chunk',
    'chunkloaderror',
    'failed to load chunk',
    'error loading chunk',
    'importing a module script failed',
    // Network errors that might be chunk-related
    'failed to fetch',
    'networkerror',
    'load failed',
  ];

  // Check if error message contains chunk error patterns
  const hasChunkErrorPattern = chunkErrorPatterns.some(pattern =>
    errorString.includes(pattern),
  );

  // Check if it's a 404 error on a .js or .css file (likely a chunk)
  const is404OnAsset =
    errorString.includes('404') &&
    (errorString.includes('.js') ||
      errorString.includes('.css') ||
      errorString.includes('chunk'));

  // Check if error name indicates chunk loading
  const isChunkErrorName =
    errorName.toLowerCase().includes('chunk') || errorName === 'ChunkLoadError';

  return hasChunkErrorPattern || is404OnAsset || isChunkErrorName;
}

/**
 * Checks if a network error is likely a chunk loading error
 * by examining the request URL
 *
 * @param url - The URL that failed to load
 * @returns true if the URL looks like a chunk file
 */
export function isChunkUrl(url: string): boolean {
  if (!url) return false;

  const urlLower = url.toLowerCase();

  // Check for common chunk file patterns
  return (
    urlLower.includes('chunk') ||
    urlLower.endsWith('.js') ||
    urlLower.endsWith('.css') ||
    // Vite chunk naming pattern: assets/[name]-[hash].js
    /assets\/.*-[a-f0-9]+\.(js|css)/.test(urlLower)
  );
}
